import React from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useMultiStyleConfig, Flex, Text, StylesProvider } from '@chakra-ui/react'

import { useListGroup, ListGroupProvider } from './useListGroup'

import ListGroupItemPlaceholder from './ListGroupItemPlaceholder'

export const ListGroupWrapper = React.forwardRef(({ dndScope, onMove, ...props }, ref) => (
  <Flex {...props} as='ol' direction='column' ref={ref} />
))

export const ListGroup = ({ isMovable, isSelectable, placeholder, children, ...props }) => {
  const styles = useMultiStyleConfig('ListGroup', props)
  const context = useListGroup({
    isMovable,
    isSelectable
  })

  const hasChildren = React.Children.toArray(children).filter(Boolean).length !== 0
  const ListGroupComponent = isMovable && hasChildren ? MovableListGroup : ListGroupWrapper

  return (
    <StylesProvider value={styles}>
      <ListGroupProvider value={context}>
        <ListGroupComponent {...props}>
          {hasChildren ? (
            children
          ) : (
            <ListGroupItemPlaceholder>
              {typeof placeholder === 'string' ? <Text>{placeholder}</Text> : placeholder}
            </ListGroupItemPlaceholder>
          )}
        </ListGroupComponent>
      </ListGroupProvider>
    </StylesProvider>
  )
}

export const MovableListGroup = ({ dndScope, onMove, children, ...props }) => {
  const onDragEnd = ({ source, destination }) => {
    if (destination) {
      onMove?.(source.index, destination.index)
    }
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={dndScope}>
        {({ innerRef, droppableProps, placeholder }, snapshot) => (
          <ListGroupWrapper ref={innerRef} {...props} {...droppableProps}>
            {React.Children.map(
              children,
              (child, index) =>
                child && (
                  <Draggable
                    key={child.props.id}
                    draggableId={`${dndScope}-${child.props.id}`}
                    index={index}>
                    {(provided, snapshot) =>
                      React.cloneElement(child, {
                        ref: provided.innerRef,
                        handleProps: provided.dragHandleProps,
                        ...provided.draggableProps
                      })
                    }
                  </Draggable>
                )
            )}
            {placeholder}
          </ListGroupWrapper>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default ListGroup
